/**
 * Adapter abstraction over connection service calls — user-scoped or group-scoped.
 * Allows ConnectorsPage to work with either user-level or sandbox-group-level connections.
 *
 * @module services/connectionServiceAdapter
 */

import { connectionService } from './connectionService';
import type {
  ConnectionResponse,
  CreateConnectionRequest,
  AuthorizeConnectionRequest,
  PolicyRule,
  ConnectionTypeResponse,
} from '@/types';

/** Abstraction over connection service calls — user-scoped or group-scoped */
export interface ConnectionServiceAdapter {
  list(): Promise<ConnectionResponse[]>;
  create(request: CreateConnectionRequest): Promise<ConnectionResponse>;
  delete(id: string, force?: boolean): Promise<void>;
  authorize(id: string, request: AuthorizeConnectionRequest): Promise<ConnectionResponse>;
  refresh(id: string): Promise<ConnectionResponse>;
  generateConsentLink(id: string, redirectUrl?: string): Promise<string>;
  updatePolicyRules(
    id: string,
    policyRules: PolicyRule[],
    enabledToolGroups?: string[]
  ): Promise<ConnectionResponse>;
  listTypes(): Promise<ConnectionTypeResponse[]>;
}

/** User-scoped adapter — delegates to connectionService.xxx() */
export function createUserScopedAdapter(): ConnectionServiceAdapter {
  return {
    list: () => connectionService.list(),
    create: (request) => connectionService.create(request),
    delete: (id, force) => connectionService.delete(id, force),
    authorize: (id, request) => connectionService.authorize(id, request),
    refresh: (id) => connectionService.refresh(id),
    generateConsentLink: (id, redirectUrl) =>
      connectionService.generateConsentLink(id, redirectUrl),
    updatePolicyRules: (id, policyRules, enabledToolGroups) =>
      connectionService.updatePolicyRules(id, policyRules, enabledToolGroups),
    listTypes: () => connectionService.listTypes(),
  };
}

/** Group-scoped adapter — delegates to connectionService.xxxForGroup(groupId, ...) */
export function createGroupScopedAdapter(sandboxGroupId: string): ConnectionServiceAdapter {
  return {
    list: () => connectionService.listForGroup(sandboxGroupId),
    create: (request) => connectionService.createForGroup(sandboxGroupId, request),
    delete: (id, force) => connectionService.deleteForGroup(sandboxGroupId, id, force),
    authorize: (id, request) => connectionService.authorizeForGroup(sandboxGroupId, id, request),
    refresh: (id) => connectionService.refreshForGroup(sandboxGroupId, id),
    generateConsentLink: (id, redirectUrl) =>
      connectionService.generateConsentLinkForGroup(sandboxGroupId, id, redirectUrl),
    updatePolicyRules: (id, policyRules, enabledToolGroups) =>
      connectionService.updatePolicyRulesForGroup(
        sandboxGroupId,
        id,
        policyRules,
        enabledToolGroups
      ),
    listTypes: () => connectionService.listTypes(),
  };
}
